import classNames from 'classnames';
import {Counter, COUNTER_SIZE} from 'components/Counter';
import {NO_IMAGE_SIZE, NoImage} from 'components/NoImage';
import {Typography} from 'components/Typography';
import {formatPrice, formatPriceNumber} from 'core/helpers';
import {useAppSelector} from 'core/redux/hooks/useAppSelector';
import {useCartProduct} from 'modules/cart/hooks/useCartProduct';
import {selectIsCartCountLoads} from 'modules/cart/selectors';
import React, {FC, MouseEventHandler, useCallback} from 'react';
import style from './style.module.scss';
import {getMediaSrc} from 'core/helpers/media';
import {useDeleteFromCartMutation, useUpdateCartMutation} from 'modules/cart/services';
import Link from 'components/CustomRoutingLink';
import {ICartData, ICartProduct} from 'modules/cart/models';
import Icon from 'components/Icon';
import {TPropsWithClassName} from 'core/types';
import {ShieldItem} from 'modules/cart/components/CartMiniProductItem/components/ShieldItem';
import {DiscountPercentLabel} from 'modules/product-card/components/DiscountPercentLabel';
import {MAX_COUNTER_VALUE} from 'core/constants';
import pick from 'lodash/pick';
import {OnlineEcomLabel} from 'modules/product-card/components/OnlineEcomLabel';
import {useCartEventAnalytics} from 'modules/products/hooks/gtag/useCartEventAnalytics';
import {withRedirectParam} from 'modules/product-card/helpers/redirect';

interface IProps extends ICartProduct, TPropsWithClassName {
    isOutOfStock?: boolean;
    isDeleteButton?: boolean;
    isCounter?: boolean;
}

// eslint-disable-next-line max-lines-per-function,complexity
export const CartMiniProductItem: FC<IProps> = ({
    isOutOfStock,
    isDeleteButton = true,
    isCounter = true,
    className,
    ...product
}) => {
    const {
        media,
        id: prodcutId,
        offerId,
        title,
        price,
        cartData = {} as ICartData,
        url,
        qtyPerUOM,
        description,
        stockAmount = MAX_COUNTER_VALUE,
    } = product;

    const {id = 0, quantity = 0, isStockAvailable} = cartData;

    const cartEventGtag = useCartEventAnalytics();

    const isLoading = useAppSelector(selectIsCartCountLoads(Number(id)));

    const isSpecPrice = Boolean(price?.isSpecPrice);
    const isOnlineEcom = Boolean(price?.isOnlineEcom);

    const [updateCart] = useUpdateCartMutation();
    const [deleteProduct] = useDeleteFromCartMutation();

    const discount = price.discount.discountValue;
    const mainPrice = price.isSpecPrice && price.specPrice ? price.specPrice : price.discount.price;
    const basePrice = price.base.price;

    const handleCartCountAddClick = useCallback(async () => {
        await updateCart({id, quantity: quantity + 1})
            .unwrap()
            .then(() => cartEventGtag(product as any, quantity + 1));
    }, [cartEventGtag, id, product, quantity, updateCart]);

    const handleCartCountRemoveClick = useCallback(async () => {
        const decreaseCount = quantity - 1;
        if (0 === decreaseCount) {
            await deleteProduct({id})
                .unwrap()
                .then(() => cartEventGtag(product as any, 0));
            return;
        }

        const updateQuantity = 1 < quantity ? quantity - 1 : 1;
        await updateCart({id, quantity: updateQuantity})
            .unwrap()
            .then(() => cartEventGtag(product as any, updateQuantity));
    }, [cartEventGtag, deleteProduct, id, product, quantity, updateCart]);

    const {handleCartCountChange, cartProductCount, handleCartCounterInputBlur} = useCartProduct({
        id,
        offerId,
    });

    const handleRemoveClick = useCallback<MouseEventHandler<HTMLButtonElement>>(async () => {
        await deleteProduct({id})
            .unwrap()
            .then(() => {
                cartEventGtag(product as any, 0);
            });
    }, [cartEventGtag, deleteProduct, id, product]);

    const isShowPricePerUnitShield = 1 < qtyPerUOM;

    const renderShields = () => {
        if (!(isShowPricePerUnitShield || isSpecPrice || isOnlineEcom)) {
            return null;
        }
        return (
            <div className={style.shieldsContainer}>
                {isShowPricePerUnitShield && (
                    <ShieldItem backgroundColor={'#F4F4F4'} color={'#8E8E93'}>
                        Цена за штуку {formatPriceNumber(price.piece.price)}
                    </ShieldItem>
                )}
                {isSpecPrice && (
                    <ShieldItem backgroundColor={'#F9DDE3'} color={'#D45179'}>
                        Спеццена
                    </ShieldItem>
                )}
                {isOnlineEcom && (
                    <ShieldItem backgroundColor={'#D7F3C1'} color={'#57966A'}>
                        <OnlineEcomLabel isAnimated={true} />
                    </ShieldItem>
                )}
            </div>
        );
    };

    const renderPrice = () => (
        <div className={style.priceContainer}>
            {isStockAvailable && (Boolean(discount) || (isSpecPrice && price.specPrice)) && (
                <div className={style.priceWithDiscountContainer}>
                    <Typography color={'gray60'} variant={'label-12-r-lt'}>
                        {formatPriceNumber(basePrice)}
                    </Typography>
                    {!isSpecPrice && (
                        <DiscountPercentLabel
                            variant={'label-12-r'}
                            borderRadius={'4px'}
                            height={'auto'}
                            style={{padding: '1px 5px'}}
                            discountValue={discount}
                            cartData={pick(cartData, ['isEcom', 'ecomName'])}
                            // флаг того, что это отображается в нотификации добавления в корзину
                            isAddToCartNotification={!isCounter && !isDeleteButton}
                        />
                    )}
                </div>
            )}
            {isStockAvailable && (
                <div
                    className={classNames(style.price, {
                        [style.priceWithDiscount]: discount || (isSpecPrice && price.specPrice),
                    })}
                >
                    <Typography variant={'price-18-b'}>
                        {formatPrice(mainPrice.toString(), {
                            maximumFractionDigits: 2,
                        })}{' '}
                        ₽
                    </Typography>
                </div>
            )}
        </div>
    );

    return (
        <div className={classNames(style.miniProductItemWrapper, className)}>
            <div className={style.miniProductContainer}>
                <div className={style.imageWrapper}>
                    {media ? (
                        <img
                            alt={title || ''}
                            className={style.image}
                            src={getMediaSrc(media.imagePreview)}
                            title={title || ''}
                        />
                    ) : (
                        <NoImage size={NO_IMAGE_SIZE.STRETCHED} />
                    )}
                </div>
                <div className={style.miniProductItemInfo}>
                    <Link href={withRedirectParam(url)}>
                        <Typography className={style.productName} element={'div'} variant={'ch-title-16-b'}>
                            {/* eslint-disable @typescript-eslint/naming-convention */}
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: title ?? '',
                                }}
                            />
                            {/* eslint-enable @typescript-eslint/naming-convention */}
                        </Typography>
                    </Link>
                    <Typography
                        className={style.miniProductItemDescription}
                        color={'gray60'}
                        element={'div'}
                        variant={'label-12-r'}
                    >
                        {description}
                    </Typography>
                    <Typography
                        className={style.miniProductItemArticle}
                        color={'gray60'}
                        element={'div'}
                        variant={'label-12-r'}
                    >
                        Артикул: {prodcutId}
                    </Typography>
                    {!isStockAvailable && (
                        <Typography
                            className={style.notInStockLabel}
                            color={'primary100'}
                            element={'div'}
                            variant={'label-12-r'}
                        >
                            Товара нет в наличии
                        </Typography>
                    )}
                    {isStockAvailable && renderShields()}
                    <div className={style.miniProductItemContainer}>
                        {renderPrice()}
                        {isCounter && isStockAvailable && (
                            <Counter
                                className={style.counter}
                                count={cartProductCount}
                                handleAddClick={handleCartCountAddClick}
                                handleChange={handleCartCountChange}
                                handleInputBlur={handleCartCounterInputBlur}
                                handleRemoveClick={handleCartCountRemoveClick}
                                isHorizontal={true}
                                isLoading={isLoading}
                                size={COUNTER_SIZE.MEDIUM}
                                maxCount={stockAmount}
                            />
                        )}
                    </div>
                    {isDeleteButton && (
                        <button className={style.deleteButton} onClick={handleRemoveClick}>
                            <Icon icon={'close-m'} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
